import { Box, Divider, Stack, Typography } from "@mui/material";
import BaseModal from "@/pages/InvoiceManagement/v2/components/BaseModal";
import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { VendorRfqDetail } from "../../types";
import { describeDelta, formatPct, formatSignedMoney, type LineQuote, type ResponseSummary } from "../../utils/response";
import { deltaColor, quantityLabel } from "./RfqLineTable";

interface ConfirmResponseModalProps {
  open: boolean;
  detail: VendorRfqDetail;
  summary: ResponseSummary;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const primaryLabelFor = (summary: ResponseSummary): string => (summary.mode === "BID" ? "Submit bid" : "Accept at asking price");

export default function ConfirmResponseModal({ open, detail, summary, submitting, onClose, onConfirm }: ConfirmResponseModalProps) {
  const quotes = new Map(summary.lines.map((quote) => [quote.line_id, quote]));
  const changedCount = summary.lines.filter((quote) => quote.changed).length;
  const label = primaryLabelFor(summary);

  return (
    <BaseModal open={open} title={`${label}?`} confirmLabel={label} loading={submitting} onClose={onClose} onConfirm={onConfirm}>
      <Typography fontSize="13px" sx={{ mb: 1.5 }}>
        {summary.mode === "BID" ? `${changedCount} of ${summary.lines.length} lines carries your own rate.` : "Every line is at the asking price."}
      </Typography>
      <Stack spacing={1}>
        {detail.lines.map((line) => {
          const quote = quotes.get(line.line_id) as LineQuote;
          return (
            <Box key={line.line_id}>
              <Typography fontSize="13px" fontWeight={600}>{`${line.room} · ${line.trade}`}</Typography>
              <Typography fontSize="12px" sx={{ color: V2_COLORS.textSecondary }}>
                {`${quantityLabel(line)} × ${formatMoney(quote.rate as number)} = ${formatMoney(quote.amount)}`}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography fontSize="13px">Your total</Typography>
        <Typography fontSize="16px" fontWeight={700}>
          {formatMoney(summary.total)}
        </Typography>
      </Stack>
      {summary.changed && (
        <Typography fontSize="12px" sx={{ color: deltaColor(summary.delta), textAlign: "right" }}>
          {`${formatSignedMoney(summary.delta)} (${formatPct(summary.deltaPct)}) ${describeDelta(summary.delta)}`}
        </Typography>
      )}
    </BaseModal>
  );
}
